import { useState } from "react";
import { LogOut, Info } from "lucide-react";
import { SecondaryAction } from "./kit";
import { signOutAndGoHome, signOutEverywhereAndGoToSignIn, SIGN_OUT_FAILED_MESSAGE } from "../lib/signOut";
import { useAuth } from "../context/AuthContext";

/**
 * Sign out and Sign out everywhere on the new-look Settings (#1135). They were
 * two blocks of the old account card; now `Sign out` stands on the face and
 * `Sign out everywhere` sits in More, and both report a failure in one slot
 * under whichever control is on screen (`settings-signout-error`, D57).
 *
 * THE LIGHT TRUST PATH IS UNCHANGED. The same shared helpers in
 * `lib/signOut.js`, both called with `{ silent: true }` so a failure is shown
 * here and not toasted; a success still toasts the shipped words (D109).
 *
 * D55: `Sign out` is not destructive and stays navy outlined. `Sign out
 * everywhere` is the alert outline, and its armed second press ("Yes, sign out
 * every device") is the alert fill. Cancel disarms and stays navy outlined.
 */
const ALERT = "var(--nw-alert)";

export const OUTLINE_ACTION_STYLE = {
  alignSelf: "flex-start",
  display: "inline-flex",
  gap: 8,
  minHeight: 44,
  padding: "0 14px",
  fontSize: 14,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)",
  color: "var(--nw-navy)",
};

const ALERT_OUTLINE = { ...OUTLINE_ACTION_STYLE, border: `1px solid ${ALERT}`, color: ALERT };

const ALERT_FILLED = { ...OUTLINE_ACTION_STYLE, background: ALERT, border: "none", color: "#FFFFFF" };

/** D57's on-page failure shape: slate ground, a 3px alert rule, an info glyph, ink text. */
export function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: `3px solid ${ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color="#B42318" />
      </span>
      <span>{children}</span>
    </div>
  );
}

/** One shared state for both controls, so the failure has one slot. */
export function useSettingsSignOut() {
  const { logout, logoutEverywhere } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [signingOutEverywhere, setSigningOutEverywhere] = useState(false);
  const [error, setError] = useState("");
  // Which control failed, so the message shows beside it: under `Sign out` on
  // the face, or under `Sign out everywhere` inside More.
  const [errorWhere, setErrorWhere] = useState("face");

  const signOut = async () => {
    setSigningOut(true);
    setError("");
    const result = await signOutAndGoHome(logout, { silent: true });
    if (result?.ok === false) {
      setSigningOut(false);
      setErrorWhere("face");
      setError(result.error || SIGN_OUT_FAILED_MESSAGE);
    }
  };

  const signOutEverywhere = async () => {
    setSigningOutEverywhere(true);
    setError("");
    const result = await signOutEverywhereAndGoToSignIn(logoutEverywhere, { silent: true });
    if (result?.ok === false) {
      setSigningOutEverywhere(false);
      setConfirming(false);
      setErrorWhere("everywhere");
      setError(result.error || SIGN_OUT_FAILED_MESSAGE);
    }
  };

  return { signingOut, signOut, confirming, setConfirming, signingOutEverywhere, signOutEverywhere, error, errorWhere };
}

export function SignOutButton({ ctl }) {
  return (
    <SecondaryAction
        onClick={ctl.signOut}
        disabled={ctl.signingOut}
        data-testid="settings-signout"
        style={{ ...OUTLINE_ACTION_STYLE, opacity: ctl.signingOut ? 0.6 : 1, cursor: ctl.signingOut ? "not-allowed" : "pointer" }}
      >
        <LogOut size={16} aria-hidden="true" />
        {ctl.signingOut ? "Signing out…" : "Sign out"}
      </SecondaryAction>
  );
}

export function SignOutEverywhere({ ctl }) {
  if (!ctl.confirming) {
    return (
      <SecondaryAction onClick={() => ctl.setConfirming(true)} data-testid="settings-signout-everywhere" style={ALERT_OUTLINE}>
        <LogOut size={16} aria-hidden="true" />
        Sign out everywhere
      </SecondaryAction>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>
        Ends your session on every device — including any where you ticked "keep me signed in", and this one. Use it
        if a device is lost, shared, or you're not sure who's signed in. You'll sign in again afterwards.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <SecondaryAction
          onClick={() => ctl.setConfirming(false)}
          disabled={ctl.signingOutEverywhere}
          data-testid="settings-signout-everywhere-cancel"
          style={{ ...OUTLINE_ACTION_STYLE, opacity: ctl.signingOutEverywhere ? 0.6 : 1 }}
        >
          Cancel
        </SecondaryAction>
        <SecondaryAction
          onClick={ctl.signOutEverywhere}
          disabled={ctl.signingOutEverywhere}
          data-testid="settings-signout-everywhere-confirm"
          style={{ ...ALERT_FILLED, opacity: ctl.signingOutEverywhere ? 0.6 : 1 }}
        >
          <LogOut size={16} aria-hidden="true" />
          {ctl.signingOutEverywhere ? "Signing out…" : "Yes, sign out every device"}
        </SecondaryAction>
      </div>
    </div>
  );
}

/** `where` is "face" or "everywhere": the slot renders only beside the control that failed. */
export function SignOutError({ ctl, where }) {
  return ctl.error && ctl.errorWhere === where ? <AlertCallout testId="settings-signout-error">{ctl.error}</AlertCallout> : null;
}
